/* Presentation metadata for `fleetAlerts`.
 *
 * WHY THIS EXISTS: an alert document carries only `type`, `message`, `busId`,
 * `timestamp` and `read` (see `useFleetAlerts`). There is no severity field and
 * no location, so severity has to be DERIVED from the type. Keeping that
 * mapping in one place means the High Risk page, the header bell and any future
 * surface all rank the same alert the same way — the alternative is three
 * screens quietly disagreeing about what counts as serious.
 *
 * The type list is the real vocabulary the product writes today:
 *   app        — incident, traffic, fuel_unverified, fuel_needs_review,
 *                fuel_efficiency_anomaly, frequent_liters_unknown,
 *                refuel_awaiting_admin, refuel_admin_confirmation,
 *                bus_route_timeout
 *   backend    — missing_refuel (fuel sweep), epra_ceiling_updated (press path)
 *   add-on sync— fuel_theft, gps_discrepancy
 *   dashboard  — refuel_risk (Zana's confirmed flag)
 *   safety     — route_ended_with_students_aboard, dropoff_disputed_by_parent
 *                (both 2026-07-30; a child's whereabouts, not a cost)
 *   app        — fuel_gauge_broken
 *   app        — trip_cover (one-trip driver cover; info, not an incident)
 *   app        — bus_stationary (2026-09-08; parked somewhere that is not a
 *                school, a stop or a fuel station)
 *   backend    — bus_signal_lost (2026-09-08; the silence watch — a tracker on
 *                an open route that has stopped reporting. ADMIN ONLY: no
 *                parent is ever told a bus has gone quiet)
 * An unknown type degrades to a warning with a humanised label rather than
 * disappearing, because a new alert nobody styled must still be visible.
 */

export type FleetSeverity = 'crit' | 'warn' | 'info';
export type FleetAlertGroup = 'incident' | 'fuel' | 'route' | 'other';

export type AlertKind = 'incident' | 'fuel' | 'route' | 'gps' | 'traffic' | 'info';

const SEVERITY: Record<string, FleetSeverity> = {
  incident: 'crit',
  fuel_theft: 'crit',

  /* Two alerts about WHERE A CHILD IS, added 2026-07-30 (Gabriel's call).
   *
   * Both sit with `incident` rather than with the fuel warnings, and the
   * distinction is not severity theatre: every other alert in this file is
   * about money or equipment, and these two are about a child who may not be
   * where their parent thinks they are. Left unranked they degraded to `warn`
   * and sorted in among "litres not recorded", which is where an alert of this
   * kind goes to die. */
  route_ended_with_students_aboard: 'crit',
  dropoff_disputed_by_parent: 'crit',

  missing_refuel: 'warn',
  refuel_risk: 'warn',
  refuel_trip_unlogged: 'warn',
  refuel_trip_stale: 'warn',
  refuel_trip_station_skipped: 'info',
  fuel_needs_review: 'warn',
  fuel_efficiency_anomaly: 'warn',
  fuel_unverified: 'warn',
  frequent_liters_unknown: 'warn',
  gps_discrepancy: 'warn',
  bus_route_timeout: 'warn',
  fuel_gauge_broken: 'warn',
  station_pump_price: 'warn',
  fuel_price_missing: 'warn',

  /* A bus that has not moved for 10 minutes somewhere that is not a school, a
   * stop or a fuel station (2026-09-08, app). Deliberately NOT `info`: the
   * console bulk-clears info alerts as routine traffic, and this is an alert
   * whose only value is that somebody looks at it. Equally it is not an
   * accusation — a bus can be parked for a hundred good reasons — so the
   * label states the fact and leaves the judgement to the reader. */
  bus_stationary: 'warn',

  /* A bus on an open route whose tracker has said nothing for two minutes
     (2026-09-08, backend silenceWatch). `warn` for the same reason as the row
     above — an alert nobody bulk-clears — and never `crit`: a Kenyan school
     route has dead spots, most of these come back on their own, and an alarm
     that reads as an emergency every time one does is an alarm that gets
     ignored on the day it is not. */
  bus_signal_lost: 'warn',

  traffic: 'info',
  refuel_awaiting_admin: 'info',
  refuel_admin_confirmation: 'info',
  // Soft notice when PEAR applies EPRA ceilings from agreeing press (not critical).
  epra_ceiling_updated: 'info',
  // A driver covering another plate for one trip — not an incident.
  trip_cover: 'info',
  // A4 — school hit plan student seat cap (backend capacityNotify).
  student_capacity_reached: 'warn',
};

const LABEL: Record<string, string> = {
  incident: 'Incident reported',
  // Phrased as what happened, not as a category. An office scanning a list at
  // 6pm should not have to decode "dropoff disputed" to learn a parent says
  // their child was left in the wrong place.
  route_ended_with_students_aboard: 'Route ended with a child on board',
  dropoff_disputed_by_parent: 'Parent says a drop-off was wrong',
  fuel_gauge_broken: 'Fuel gauge not working',
  station_pump_price: 'Station pump price off',
  fuel_price_missing: 'Fuel prices missing',
  fuel_theft: 'Possible fuel loss',
  missing_refuel: 'No refill logged',
  refuel_risk: 'Refuel needs chasing',
  refuel_trip_unlogged: 'Refuel trip, no fuel logged',
  refuel_trip_stale: 'Refuel trip still open',
  refuel_trip_station_skipped: 'Station stop, no fuel logged',
  fuel_needs_review: 'Fuel needs review',
  fuel_efficiency_anomaly: 'Burning more than usual',
  fuel_unverified: 'Fuel not verified',
  frequent_liters_unknown: 'Litres not recorded',
  gps_discrepancy: 'GPS and sensor disagree',
  bus_route_timeout: 'Route never finished',
  bus_stationary: 'Bus stationary',
  bus_signal_lost: 'No location from this bus',
  traffic: 'Traffic delay',
  refuel_awaiting_admin: 'Refuel awaiting confirmation',
  refuel_admin_confirmation: 'Refuel confirmed',
  epra_ceiling_updated: 'Fuel ceilings updated',
  trip_cover: 'Driver covering another bus',
  student_capacity_reached: 'Student seat limit reached',
};

const KIND: Record<string, AlertKind> = {
  incident: 'incident',
  route_ended_with_students_aboard: 'incident',
  dropoff_disputed_by_parent: 'incident',
  fuel_gauge_broken: 'fuel',
  station_pump_price: 'fuel',
  fuel_price_missing: 'fuel',
  fuel_theft: 'fuel',
  missing_refuel: 'fuel',
  refuel_risk: 'fuel',
  refuel_trip_unlogged: 'fuel',
  refuel_trip_stale: 'fuel',
  refuel_trip_station_skipped: 'fuel',
  fuel_needs_review: 'fuel',
  fuel_efficiency_anomaly: 'fuel',
  fuel_unverified: 'fuel',
  frequent_liters_unknown: 'fuel',
  refuel_awaiting_admin: 'fuel',
  refuel_admin_confirmation: 'fuel',
  epra_ceiling_updated: 'fuel',
  /* Derived in HighRiskScreen rather than stored in `fleetAlerts` — they are
     configuration gaps, not events. Listed here only so they draw the fuel
     pump like every other fuel row; without an entry they fell through to the
     generic info icon while sitting under the Fuel filter. */
  missing_tank_capacity: 'fuel',
  missing_odometer_baseline: 'fuel',
  gps_discrepancy: 'gps',
  bus_route_timeout: 'route',
  bus_stationary: 'route',
  /* `gps`, not `route`: the route is fine, the tracker is not — and it puts the
     row under the same filter and icon as gps_discrepancy, which is the other
     alert about a tracker rather than a journey. */
  bus_signal_lost: 'gps',
  traffic: 'traffic',
  trip_cover: 'route',
  student_capacity_reached: 'info',
};

/** Severity for an alert type. Unknown types are warnings — never hidden. */
export function severityOf(type: string): FleetSeverity {
  return SEVERITY[type] ?? 'warn';
}

/** Human title. Unknown types fall back to their own name, made readable. */
export function labelOf(type: string): string {
  if (LABEL[type]) return LABEL[type];
  const words = (type || 'alert').replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function kindOf(type: string): AlertKind {
  return KIND[type] ?? 'info';
}

/** Which filter chip an alert belongs under. */
export function groupOf(type: string): FleetAlertGroup {
  const k = kindOf(type);
  if (k === 'incident') return 'incident';
  if (k === 'fuel') return 'fuel';
  if (k === 'route' || k === 'gps' || k === 'traffic') return 'route';
  return 'other';
}

/** Sort key: worst first, then newest first. */
export function severityRank(sev: FleetSeverity): number {
  return sev === 'crit' ? 0 : sev === 'warn' ? 1 : 2;
}

/**
 * "14 min ago" / "3 h ago" / "Yesterday 17:20" / "28 Jul".
 * A raw `toLocaleString()` makes the reader do arithmetic to answer the only
 * question that matters at a glance: is this happening now?
 */
export function relativeTime(d: Date, now: Date = new Date()): string {
  const ms = now.getTime() - d.getTime();
  if (!Number.isFinite(ms)) return '';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24 && d.toDateString() === now.toDateString()) return `${hrs} h ago`;
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) {
    return `Yesterday ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }
  return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

/** Day bucket used to group the feed: "Today · 29 July". */
export function dayLabel(d: Date, now: Date = new Date()): string {
  if (d.toDateString() === now.toDateString()) {
    return `Today · ${d.toLocaleDateString([], { day: 'numeric', month: 'long' })}`;
  }
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) {
    return `Yesterday · ${d.toLocaleDateString([], { day: 'numeric', month: 'long' })}`;
  }
  return d.toLocaleDateString([], { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Two-letter monogram for the bus avatar (never longer than 3 chars). */
export function monogram(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'BUS';
  if (parts.length === 1) return parts[0].slice(0, 3).toUpperCase();
  const digits = parts[parts.length - 1].replace(/\D/g, '');
  if (digits) return (parts[0][0] + digits).slice(0, 3).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/* ── WHY a refuel was flagged ───────────────────────────────────────────────
   The mobile app writes `reviewReason` onto every `fuel_needs_review` alert,
   and until 2026-08-30 nothing on the dashboard read it. The app's own message
   switch covers 4 of the 8 reasons it can emit; the other 4 — all three
   odometer verdicts and `spans_unmetered_fill` — fall to a default that says
   only "this refuel needs review". Gabriel drove a real refuel trip, entered a
   true odometer reading against stale test data, and got exactly that: an alert
   headed "Fuel needs review" for a problem that was not about fuel, with no
   sentence explaining it.

   The reason is on the document. This table is what turns it back into words,
   and it repairs alerts ALREADY WRITTEN — no app build, no re-run.

   ⚠️ WORDING IS CONSTRAINED. Alert text is regex-classified for Zana in
   pear-payments-backend/src/premium/aggregate.js (`alertKind`), which matches
   on the message body with no word boundaries. A sentence containing gps,
   speed, signal, zone, geofence, offline, late, delay, incident, breakdown,
   flat, tyre or tire is silently re-categorised as a different KIND of alert.
   Note `late` has no boundary either — so "plate" would do it. Keep new copy
   clear of all of them, here and in the app, so the two surfaces can share
   these exact strings. */
export const REVIEW_REASON_COPY: Record<string, string> = {
  // Litres-vs-distance — the four the app already words for itself.
  exceeds_tank_capacity: 'the litres entered are more than the tank holds',
  implausible_physics: 'the fuel economy for this fill sits outside what this vehicle does',
  unexpected_litres_low: 'fewer litres than the distance suggests',
  unexpected_litres_high: 'more litres than the distance suggests',
  // The odometer verdicts — from odometerReviewReason() in the app. These are
  // the four that reach the owner as a blank "this refuel needs review".
  odometer_regression: 'the odometer reads lower than it did at the previous fill',
  odometer_below_gps_km: 'the odometer moved less than the distance actually tracked for the same period',
  odometer_implausible: 'the odometer jumped further than this vehicle could have covered',
  spans_unmetered_fill: 'a fill in between was logged with no litres, so this distance covers more than one tank',
};

/** The app's contentless fallback, verbatim. An alert whose message is only
 *  this carries no information the owner can act on — the reason has to come
 *  from `reviewReason` instead. Matched loosely: the app prefixes the bus. */
export function isBareReviewMessage(message: string): boolean {
  return /this refuel needs review\.?\s*$/i.test((message || '').trim());
}

/**
 * The sentence to show for a flagged refuel.
 *
 * Prefers whatever the app wrote — it may carry numbers this table cannot know.
 * Falls back to the reason only when the message says nothing, so a good
 * message is never overwritten by a generic one.
 */
export function reviewReasonSentence(
  message: string,
  reviewReason?: string | null,
): string | null {
  const copy = reviewReason ? REVIEW_REASON_COPY[reviewReason] : undefined;
  if (!copy) return null;
  return isBareReviewMessage(message) ? copy : null;
}
